/*
 * LumvinWalls installation guide — <lumvin-install-guide>.
 *
 * Renders the manufacturer's installation instructions (the producer's booklet,
 * https://online.fliphtml5.com/fsbat/boms/) as an interactive, material-specific guide for the
 * `lumvin-installation-guide` section (templates/page.installation-guide.json).
 *
 * Source lock: the official flow (FLOWS) contains only booklet instructions and booklet images.
 * Every step lists the booklet pages it comes from. LumvinWalls' own help is rendered after it
 * under a separate heading, followed by the original booklet itself.
 *
 * - Copy lives in lumvin-install-guide-i18n-<lang>.js; `official` there is the booklet text in
 *   that language. Only the selected language (plus the English fallback) is downloaded.
 * - Language: ?lang= → saved choice → browser language → section default. Material: ?material=
 *   → saved choice. Both are saved in localStorage and mirrored in the URL without touching
 *   other query parameters (UTM tags survive).
 * - Images open larger in a native <dialog>. Without JavaScript the section shows a short
 *   <noscript> notice with links.
 * - Analytics: interactions are dispatched as a `lumvin:install-guide` DOM event and forwarded to
 *   Shopify customer events (`Shopify.analytics.publish`) and `window.dataLayer` when present.
 */

const STORAGE_KEYS = { language: 'lumvin:install-guide:language', material: 'lumvin:install-guide:material' };

/* ------------------------------------------------------------------ */
/* Booklet mapping                                                     */
/* ------------------------------------------------------------------ */

/**
 * Customer-facing material names are the Shopify "Material" option values and stay untranslated.
 * `method` selects the booklet instructions: `paste` (the booklet's Non-woven / Textured / Textile
 * pages, which carry the same text) or `self` (its Self-adhesive page). `booklet` is the booklet
 * page type for the material. Canvas Peel & Stick is self-adhesive in the store; whether it is the
 * booklet's "Textile" (a pasted material) is not confirmed by the manufacturer yet.
 */
const MATERIALS = [
  { id: 'non-woven', key: 'nonwoven', name: 'Non-Woven', method: 'paste', booklet: 'nonwoven' },
  { id: 'textured', key: 'textured', name: 'Textured', method: 'paste', booklet: 'textured' },
  { id: 'peel-and-stick', key: 'peel', name: 'Peel & Stick', method: 'self', booklet: 'self' },
  { id: 'canvas-peel-and-stick', key: 'canvas', name: 'Canvas Peel & Stick', method: 'self', booklet: 'self' },
];

/** Booklet page numbers as the flipbook counts them (its `#p=` anchor), per language section. */
const BOOKLET_PAGES = {
  cover: { en: 3, es: 9, fr: 15, de: 21, tr: 27 },
  textile: { en: 4, es: 10, fr: 16, de: 22, tr: 28 },
  textured: { en: 5, es: 11, fr: 17, de: 23, tr: 29 },
  nonwoven: { en: 6, es: 12, fr: 18, de: 24, tr: 30 },
  self: { en: 7, es: 13, fr: 19, de: 25, tr: 31 },
  photo: { en: 8, es: 14, fr: 20, de: 26, tr: 32 },
};

/**
 * Booklet pages printed with the wrong text. Steps marked `misprint` cite the English page
 * instead, and the bundle's `official.translated` note is shown.
 */
const MISPRINTED = { self: ['fr', 'tr'] };

/* ------------------------------------------------------------------ */
/* Booklet images                                                      */
/* ------------------------------------------------------------------ */

/**
 * Instruction photos and drawings from the booklet, reused with the producer's permission
 * (photos and overlap examples: photo page; drawings: material pages). The section passes each
 * file's CDN URL in `config.images`; keep this list and the section's file list in sync. Photos
 * and drawings are 4x upscales of the booklet originals (Protsy: SeedVR for photos, Topaz for
 * drawings) at 1000 px, with the drawings' step badges and upscaler artefacts removed.
 */
const IMAGES = {
  tools: { file: 'lw-install-photo-tools.jpg', width: 2000, height: 1516 },
  cleanWall: { file: 'lw-install-photo-clean-wall.jpg', width: 2000, height: 1604 },
  markLines: { file: 'lw-install-photo-mark-lines.jpg', width: 2000, height: 1570 },
  mixPaste: { file: 'lw-install-photo-mix-paste.jpg', width: 2000, height: 1586 },
  firstStrip: { file: 'lw-install-photo-first-strip.jpg', width: 2000, height: 1586 },
  secondPanel: { file: 'lw-install-photo-second-panel.jpg', width: 2000, height: 1576 },
  pasteWall: { file: 'lw-install-paste-1-paste-wall.png', width: 2000, height: 1282 },
  pasteHang: { file: 'lw-install-paste-2-hang.png', width: 2000, height: 1212 },
  pasteCut: { file: 'lw-install-paste-3-cut.png', width: 2000, height: 1274 },
  pasteSmooth: { file: 'lw-install-paste-4-smooth.png', width: 2000, height: 1282 },
  pasteTrim: { file: 'lw-install-paste-5-trim.png', width: 2000, height: 1212 },
  pasteDone: { file: 'lw-install-paste-6-finished.png', width: 2000, height: 1274 },
  peelHang: { file: 'lw-install-peel-1-hang.png', width: 2000, height: 1444 },
  peelCut: { file: 'lw-install-peel-2-detail.png', width: 2000, height: 1444 },
  peelSmooth: { file: 'lw-install-peel-3-smooth.png', width: 2000, height: 1444 },
  peelTrim: { file: 'lw-install-peel-4-trim.png', width: 2000, height: 1444 },
  peelDone: { file: 'lw-install-peel-5-finished.png', width: 2000, height: 1444 },
};

/**
 * The booklet's overlap examples, cut from each language's photo page so they carry its own label
 * (Turkish without its mislabelled caption), upscaled to 1800 px; the labels were checked letter by letter.
 * Files: lw-install-overlap-{correct|wrong}-{lang}.jpg.
 */
/** Widths offered in each booklet image's srcset, below its full size. */
const IMAGE_WIDTHS = [600, 900, 1200];

const OVERLAP_EXAMPLES = {
  en: { right: [1800, 1236], wrong: [1800, 1270] },
  es: { right: [1800, 1230], wrong: [1800, 1264] },
  fr: { right: [1800, 1232], wrong: [1800, 1270] },
  de: { right: [1800, 1230], wrong: [1800, 1264] },
  tr: { right: [1800, 982], wrong: [1800, 1012] },
};

/* ------------------------------------------------------------------ */
/* Official flows                                                      */
/* ------------------------------------------------------------------ */

/*
 * Each step: `title` (official.titles key), `sources` (booklet page types: `material` is the
 * selected material's page, `photo` the photo page), optional `misprint` (the material text is
 * affected by MISPRINTED) and `blocks`:
 *   { text: 'official.paste.preparation' }          booklet paragraphs
 *   { tools: true }                                  the booklet's tool list for the method
 *   { note: 'official.recommend' }                   a booklet remark
 *   { frames: [[imageKey, captionKey], …] }          booklet images with booklet captions
 *   { overlap: true }                                the booklet's overlap examples and text
 * Caption keys point to a string or to { title, text }.
 */
const FLOWS = {
  paste: [
    {
      id: 'tools',
      title: 'tools',
      sources: ['material', 'photo'],
      blocks: [{ note: 'official.recommend' }, { tools: true }, { frames: [['tools', 'official.photo.p1']] }],
    },
    {
      id: 'preparation',
      title: 'preparation',
      sources: ['material', 'photo'],
      blocks: [
        { text: 'official.paste.preparation' },
        { frames: [['cleanWall', 'official.photo.p2'], ['markLines', 'official.photo.p3']] },
      ],
    },
    { id: 'wallpaper', title: 'wallpaper', sources: ['material'], blocks: [{ text: 'official.paste.wallpaper' }] },
    {
      id: 'adhesive',
      title: 'adhesive',
      sources: ['material', 'photo'],
      blocks: [
        { text: 'official.paste.adhesive' },
        { frames: [['mixPaste', 'official.photo.p4'], ['pasteWall', 'official.drawings.pasteWall']] },
      ],
    },
    {
      id: 'application',
      title: 'application',
      sources: ['material', 'photo'],
      blocks: [
        { text: 'official.paste.application' },
        {
          frames: [
            ['firstStrip', 'official.photo.p5'],
            ['pasteHang', 'official.drawings.pasteHang'],
            ['pasteSmooth', 'official.drawings.pasteSmooth'],
          ],
        },
      ],
    },
    {
      id: 'overlap',
      title: 'overlap',
      sources: ['photo'],
      blocks: [
        { overlap: true },
        { frames: [['secondPanel', 'official.photo.p6'], ['pasteDone', 'official.drawings.pasteDone']] },
      ],
    },
    {
      id: 'double-cut',
      title: 'doubleCut',
      sources: ['material'],
      optional: true,
      blocks: [{ text: 'official.paste.doubleCut' }, { frames: [['pasteCut', 'official.drawings.pasteCut']] }],
    },
    {
      id: 'finishing',
      title: 'finishing',
      sources: ['material'],
      blocks: [{ text: 'official.paste.finishing' }, { frames: [['pasteTrim', 'official.drawings.pasteTrim']] }],
    },
  ],

  self: [
    { id: 'tools', title: 'tools', sources: ['material'], misprint: true, blocks: [{ note: 'official.recommend' }, { tools: true }] },
    {
      id: 'preparation',
      title: 'preparation',
      sources: ['material', 'photo'],
      blocks: [{ text: 'official.self.preparation' }, { frames: [['cleanWall', 'official.photo.p2']] }],
    },
    { id: 'wallpaper', title: 'wallpaper', sources: ['material'], blocks: [{ text: 'official.self.wallpaper' }] },
    { id: 'work-area', title: 'workArea', sources: ['material'], misprint: true, blocks: [{ text: 'official.self.workArea' }] },
    {
      id: 'guideline',
      title: 'guideline',
      sources: ['material', 'photo'],
      misprint: true,
      blocks: [{ text: 'official.self.guideline' }, { frames: [['markLines', 'official.photo.p3']] }],
    },
    {
      id: 'pre-cut',
      title: 'precut',
      sources: ['material'],
      misprint: true,
      blocks: [{ text: 'official.self.precut' }, { frames: [['peelCut', 'official.drawings.peelCut']] }],
    },
    {
      id: 'first-strip',
      title: 'firstStrip',
      sources: ['material', 'photo'],
      misprint: true,
      blocks: [
        { text: 'official.self.firstStrip' },
        { frames: [['peelHang', 'official.drawings.peelHang']] },
        { note: 'official.photo.p4self' },
      ],
    },
    {
      id: 'smooth-trim',
      title: 'smoothTrim',
      sources: ['material'],
      misprint: true,
      blocks: [
        { text: 'official.self.smoothTrim' },
        { frames: [['peelSmooth', 'official.drawings.peelSmooth'], ['peelTrim', 'official.drawings.peelTrim']] },
      ],
    },
    {
      id: 'overlap',
      title: 'overlap',
      sources: ['material', 'photo'],
      misprint: true,
      blocks: [
        { text: 'official.self.repeat' },
        { overlap: true },
        { frames: [['secondPanel', 'official.photo.p6'], ['peelDone', 'official.drawings.peelDone']] },
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** @param {unknown} value */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ESCAPES[char]);
}

/**
 * @param {object} bundle
 * @param {string} path
 */
function lookup(bundle, path) {
  return path.split('.').reduce((node, part) => (node == null ? undefined : node[part]), bundle);
}

/** @param {string} value */
function fill(value, vars) {
  return value.replace(/\{(\w+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match));
}

/**
 * Picks the first supported language from a list of BCP 47 tags.
 * @param {readonly string[]} candidates
 * @param {string[]} supported
 */
export function matchLanguage(candidates, supported) {
  for (const candidate of candidates) {
    const base = String(candidate || '').toLowerCase().split('-')[0];
    if (supported.includes(base)) return base;
  }
  return null;
}

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the choice simply isn't remembered.
  }
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ICONS = {
  check: '<path d="M5 12.5 l4 4 L19 7"/>',
  cross: '<path d="M6 6 L18 18 M18 6 L6 18"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/>',
  zoom: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5 L21 21 M10.5 7.5 v6 M7.5 10.5 h6"/>',
  play: '<path d="M8 5 v14 l11 -7 z"/>',
  arrowUp: '<path d="M6 15 l6 -6 l6 6"/>',
  arrowDown: '<path d="M6 9 l6 6 l6 -6"/>',
  external: '<path d="M14 4 h6 v6 M20 4 L11 13 M18 14 v5 a1 1 0 0 1 -1 1 H5 a1 1 0 0 1 -1 -1 V7 a1 1 0 0 1 1 -1 h5"/>',
  book: '<path d="M4 5 a2 2 0 0 1 2 -2 h13 v16 H6 a2 2 0 0 0 -2 2 z"/><path d="M4 19 V5"/><path d="M8 7 h7"/>',
};

/** @param {string} name */
function icon(name, className = '') {
  return `<svg class="lumvin-guide__icon ${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${ICONS[name]}</svg>`;
}

/** Placeholder material swatches for the material cards. */
const SWATCHES = {
  nonwoven: `<rect width="120" height="90" class="ig-swatch-base"/>${Array.from({ length: 22 }, (_, i) => `<path class="ig-fiber" d="M${(i * 37) % 120} ${(i * 23) % 90} q12 ${i % 2 ? 6 : -6} 24 0"/>`).join('')}`,
  textured: `<rect width="120" height="90" class="ig-swatch-base"/>${Array.from({ length: 9 }, (_, row) => Array.from({ length: 12 }, (_, col) => `<circle class="ig-relief" cx="${col * 10 + (row % 2) * 5 + 5}" cy="${row * 10 + 5}" r="2.6"/>`).join('')).join('')}`,
  peel: `<rect width="120" height="90" class="ig-swatch-base"/><path class="ig-backing" d="M70 90 L120 40 V90 Z"/><path class="ig-swatch-edge" d="M70 90 L120 40"/><path class="ig-swatch-curl" d="M84 90 C96 74 108 60 120 52 V90 Z"/>`,
  canvas: `<rect width="120" height="90" class="ig-swatch-base"/>${Array.from({ length: 30 }, (_, i) => `<path class="ig-weave" d="M0 ${i * 3} H120"/>`).join('')}${Array.from({ length: 40 }, (_, i) => `<path class="ig-weave" d="M${i * 3} 0 V90"/>`).join('')}`,
};

/** Fallback language names when a bundle hasn't loaded yet (the switcher shows every language). */
const LANGUAGE_NAMES = { en: 'English', de: 'Deutsch', tr: 'Türkçe', fr: 'Français', es: 'Español' };

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/**
 * @typedef {object} GuideConfig
 * @property {Record<string, string>} languages - Language code → bundle URL.
 * @property {string} defaultLanguage
 * @property {string} contactUrl
 * @property {string} email
 * @property {string} bookletUrl - The manufacturer's original booklet.
 * @property {Record<string, string>} images - Image file name → CDN URL.
 * @property {Record<string, { image?: { src: string, srcset: string, width: number, height: number, alt: string }, video?: { embed: string, title: string, duration: string } }>} media
 */

class LumvinInstallGuide extends HTMLElement {
  /** @type {GuideConfig} */
  #config;
  /** @type {Record<string, object>} */
  #bundles = {};
  #language = 'en';
  /** @type {typeof MATERIALS[number] | null} */
  #material = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {IntersectionObserver | null} */
  #barObserver = null;
  #viewedSteps = new Set();
  #activeStep = '';
  /** @type {HTMLElement | null} */
  #lightboxTrigger = null;
  #abort = new AbortController();

  async connectedCallback() {
    try {
      this.#config = JSON.parse(this.querySelector('[data-guide-config]')?.textContent || '{}');
    } catch {
      return;
    }

    const supported = Object.keys(this.#config.languages || {});
    if (!supported.length) return;

    const params = new URLSearchParams(window.location.search);
    this.#language =
      matchLanguage([params.get('lang') || '', readStorage(STORAGE_KEYS.language) || ''], supported) ||
      matchLanguage(navigator.languages || [navigator.language], supported) ||
      (supported.includes(this.#config.defaultLanguage) ? this.#config.defaultLanguage : supported[0]);

    const materialId = params.get('material') || readStorage(STORAGE_KEYS.material);
    this.#material = MATERIALS.find((material) => material.id === materialId) ?? null;

    try {
      await this.#loadBundles(this.#language);
    } catch {
      return;
    }

    this.#render();
    this.#bindEvents();
    this.classList.add('is-ready');

    const hashStep = window.location.hash.replace('#step-', '');
    if (this.#material && hashStep) this.#scrollToStep(hashStep, false);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#barObserver?.disconnect();
  }

  /** @param {string} language */
  async #loadBundles(language) {
    const load = async (code) => {
      if (!this.#bundles[code]) this.#bundles[code] = (await import(this.#config.languages[code])).default;
    };
    await Promise.all([load('en'), load(language)]);
  }

  /** Raw value with English fallback. @param {string} key */
  #raw = (key) => lookup(this.#bundles[this.#language], key) ?? lookup(this.#bundles.en, key);

  /** Unescaped, interpolated string. @param {string} key */
  #rawT = (key, vars = {}) => {
    const value = this.#raw(key);
    return typeof value === 'string' ? fill(value, vars) : key;
  };

  /** Escaped, interpolated string for attribute values. @param {string} key */
  #attr = (key, vars = {}) => escapeHtml(this.#rawT(key, vars));

  /**
   * Escaped, interpolated string for HTML. The brand name is marked `lang="en"` so uppercase
   * styles don't turn it into "LUMVİNWALLS" in Turkish.
   * @param {string} key
   */
  #t = (key, vars = {}) => {
    const text = escapeHtml(this.#rawT(key, vars));
    if (!text.includes('LumvinWalls')) return text;
    // One wrapper keeps the spaces around the brand when the text sits in a flex container.
    return `<span>${text.replace(/LumvinWalls/g, '<span lang="en">LumvinWalls</span>')}</span>`;
  };

  get #steps() {
    return this.#material ? FLOWS[this.#material.method] : [];
  }

  /** @param {typeof FLOWS.paste[number]} step */
  #stepTitle(step) {
    return this.#rawT(`official.titles.${step.title}`);
  }

  /** Whether the selected language's booklet page for this material is misprinted. */
  get #misprinted() {
    return Boolean(this.#material && MISPRINTED[this.#material.booklet]?.includes(this.#language));
  }

  /**
   * Booklet pages a step comes from, in the selected language.
   * @param {typeof FLOWS.paste[number]} step
   * @returns {{ page: number, label: string }[]}
   */
  #sourcePages(step) {
    const material = this.#material;
    if (!material) return [];
    return step.sources.map((source) => {
      if (source === 'photo') {
        const page = BOOKLET_PAGES.photo[this.#language];
        return { page, label: String(page) };
      }
      const language = step.misprint && this.#misprinted ? 'en' : this.#language;
      const page = BOOKLET_PAGES[material.booklet][language];
      return { page, label: language === this.#language ? String(page) : this.#rawT('official.englishPage', { page }) };
    });
  }

  /** @param {number} page */
  #bookletLink(page) {
    return `${this.#config.bookletUrl}#p=${page}`;
  }

  /** Booklet pages for the selected material, e.g. [6, 8]. */
  get #materialPages() {
    const pages = new Set();
    for (const step of this.#steps) for (const source of this.#sourcePages(step)) pages.add(source.page);
    return [...pages].sort((a, b) => a - b);
  }

  /**
   * A booklet image, or '' when its URL is missing.
   * @param {string} file
   * @param {number} width
   * @param {number} height
   * @param {string} alt
   */
  #img(file, width, height, alt) {
    const src = this.#config.images?.[file];
    if (!src) return '';
    // Theme assets resize on Shopify's CDN through the `width` parameter.
    const resized = (w) => `${src}${src.includes('?') ? '&' : '?'}width=${w}`;
    const srcset = [...IMAGE_WIDTHS.filter((w) => w < width).map((w) => `${resized(w)} ${w}w`), `${src} ${width}w`].join(', ');
    return `<img class="lumvin-guide__photo" src="${escapeHtml(src)}" srcset="${escapeHtml(srcset)}" sizes="(min-width: 750px) 360px, calc(100vw - 32px)" width="${width}" height="${height}" alt="${escapeHtml(alt)}" loading="lazy" decoding="async">`;
  }

  /** Caption from a key that holds a string or { title, text }. @param {string} key */
  #caption(key) {
    const value = this.#raw(key);
    if (value && typeof value === 'object') return { title: value.title ?? '', text: value.text ?? '' };
    return { title: '', text: typeof value === 'string' ? value : '' };
  }

  /**
   * Large image + caption cards; frames whose image is unavailable are skipped.
   * @param {{ visual: string, title?: string, text: string, tone?: 'ok' | 'no' }[]} items
   */
  #frames(items, className = '') {
    const shown = items.filter((item) => item.visual);
    if (!shown.length) return '';
    return `<ul class="lumvin-guide__frames ${className}" role="list">${shown
      .map(
        (item) => `<li class="lumvin-guide__frame${item.tone ? ` lumvin-guide__frame--${item.tone}` : ''}">
          <figure>
            <button type="button" class="lumvin-guide__zoom" data-zoom aria-label="${escapeHtml(item.title || item.text)}">${item.visual}${icon('zoom', 'lumvin-guide__zoom-icon')}</button>
            <figcaption>${item.title ? `<strong>${escapeHtml(item.title)}</strong>` : ''}<span>${escapeHtml(item.text)}</span></figcaption>
          </figure>
        </li>`
      )
      .join('')}</ul>`;
  }

  /** @param {object} block */
  #renderBlock(block) {
    if (block.text) {
      const paragraphs = this.#raw(block.text) ?? [];
      return `<div class="lumvin-guide__paras">${paragraphs.map((text) => `<p>${escapeHtml(text)}</p>`).join('')}</div>`;
    }
    if (block.note) {
      const { title, text } = this.#caption(block.note);
      return `<div class="lumvin-guide__callout" role="note">${icon('info')}<div>${title ? `<p class="lumvin-guide__callout-title">${escapeHtml(title)}</p>` : ''}<p>${escapeHtml(text)}</p></div></div>`;
    }
    if (block.tools) {
      const tools = this.#raw(`official.tools.${this.#material.method}`) ?? [];
      return `<ul class="lumvin-guide__tools" role="list">${tools.map((tool) => `<li>${icon('check')}<span>${escapeHtml(tool)}</span></li>`).join('')}</ul>`;
    }
    if (block.frames) {
      return this.#frames(
        block.frames.map(([key, captionKey]) => {
          const image = IMAGES[key];
          const { title, text } = this.#caption(captionKey);
          return { visual: this.#img(image.file, image.width, image.height, title || text), title, text };
        })
      );
    }
    if (block.overlap) {
      const sizes = OVERLAP_EXAMPLES[this.#language] ?? OVERLAP_EXAMPLES.en;
      const language = OVERLAP_EXAMPLES[this.#language] ? this.#language : 'en';
      const example = (kind) => {
        const label = this.#rawT(`official.photo.${kind}`);
        const file = `lw-install-overlap-${kind === 'right' ? 'correct' : 'wrong'}-${language}.jpg`;
        return { visual: this.#img(file, sizes[kind][0], sizes[kind][1], label), text: label, tone: kind === 'right' ? 'ok' : 'no' };
      };
      const { title, text } = this.#caption('official.photo.overlap');
      return `<div class="lumvin-guide__overlap">
        <p class="lumvin-guide__overlap-text"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(text)}</span></p>
        ${this.#frames([example('right'), example('wrong')], 'lumvin-guide__frames--pair')}
      </div>`;
    }
    return '';
  }

  /** Merchant photo for a step (added in the theme editor), shown before the booklet content. */
  #media(slot) {
    const image = this.#config.media?.[slot]?.image;
    if (!image) return '';
    return `<figure class="lumvin-guide__figure">
      <img src="${escapeHtml(image.src)}" srcset="${escapeHtml(image.srcset)}" sizes="(min-width: 990px) 640px, 100vw"
        width="${image.width}" height="${image.height}" alt="${escapeHtml(image.alt)}" loading="lazy" decoding="async">
    </figure>`;
  }

  /** Click-to-load video teaser; renders nothing without a configured video. */
  #video(slot, stepTitle) {
    const video = this.#config.media?.[slot]?.video;
    if (!video?.embed) return '';
    const title = video.title || stepTitle;
    return `<div class="lumvin-guide__video" data-video-slot="${escapeHtml(slot)}">
      <button type="button" class="lumvin-guide__video-button" data-video-embed="${escapeHtml(video.embed)}" data-video-title="${escapeHtml(this.#rawT('guide.videoTitle', { title }))}">
        ${icon('play')}
        <span>${this.#t('guide.watch', { title })}${video.duration ? ` <span class="lumvin-guide__video-duration">— ${escapeHtml(video.duration)}</span>` : ''}</span>
      </button>
    </div>`;
  }

  #render() {
    const app = this.querySelector('[data-guide-app]');
    if (!app) return;

    app.lang = this.#language;
    app.innerHTML = `${this.#renderIntro()}${this.#material ? this.#renderGuide() : ''}${this.#renderBooklet()}
      <dialog class="lumvin-guide__lightbox" data-lightbox aria-labelledby="LumvinGuideLightboxCaption">
        <div class="lumvin-guide__lightbox-inner">
          <button type="button" class="lumvin-guide__lightbox-close" data-lightbox-close aria-label="${this.#attr('guide.close')}">${icon('cross')}</button>
          <div class="lumvin-guide__lightbox-body" data-lightbox-body></div>
          <p class="lumvin-guide__lightbox-caption" id="LumvinGuideLightboxCaption" data-lightbox-caption></p>
        </div>
      </dialog>`;

    this.#observeSteps();
  }

  #renderIntro() {
    const languages = Object.keys(this.#config.languages);
    return `<header class="lumvin-guide__intro">
      <p class="lumvin-eyebrow lumvin-eyebrow--dot">${this.#t('guide.eyebrow')}</p>
      <h1 class="lumvin-guide__title">${this.#t('guide.title')}</h1>
      <p class="lumvin-guide__intro-text">${this.#t('guide.intro')}</p>

      <div class="lumvin-guide__languages" role="group" aria-labelledby="LumvinGuideLanguageLabel">
        <p class="lumvin-guide__label" id="LumvinGuideLanguageLabel">${this.#t('guide.chooseLanguage')}</p>
        <div class="lumvin-guide__language-list">
          ${languages
            .map(
              (code) => `<button type="button" class="lumvin-guide__language" lang="${code}" data-language="${code}" aria-pressed="${code === this.#language}">
                ${escapeHtml(lookup(this.#bundles[code], 'meta.name') ?? LANGUAGE_NAMES[code] ?? code)}
              </button>`
            )
            .join('')}
        </div>
      </div>
    </header>

    <section class="lumvin-guide__materials" aria-labelledby="LumvinGuideMaterialHeading">
      <h2 class="lumvin-guide__heading" id="LumvinGuideMaterialHeading">${this.#t('guide.chooseMaterial')}</h2>
      <p class="lumvin-guide__hint">${this.#t('guide.chooseMaterialHint')}</p>
      <div class="lumvin-guide__material-list" role="list">
        ${MATERIALS.map((material) => {
          const image = this.#config.media?.[`material-${material.key}`]?.image;
          const visual = image
            ? `<img src="${escapeHtml(image.src)}" srcset="${escapeHtml(image.srcset)}" sizes="(min-width: 750px) 25vw, 50vw" width="${image.width}" height="${image.height}" alt="" loading="lazy">`
            : `<svg viewBox="0 0 120 90" aria-hidden="true" focusable="false" preserveAspectRatio="xMidYMid slice">${SWATCHES[material.key]}</svg>`;
          const selected = this.#material?.id === material.id;
          return `<div role="listitem">
            <button type="button" class="lumvin-guide__material${selected ? ' is-selected' : ''}" data-material="${material.id}" aria-pressed="${selected}">
              <span class="lumvin-guide__swatch">${visual}</span>
              <span class="lumvin-guide__material-body">
                <span class="lumvin-guide__material-name">${escapeHtml(material.name)}</span>
                <span class="lumvin-guide__material-method">${this.#t(`materials.${material.method}`)}</span>
              </span>
              <span class="lumvin-guide__material-check" aria-hidden="true">${icon('check')}</span>
            </button>
          </div>`;
        }).join('')}
      </div>
    </section>`;
  }

  /** @param {number[]} pages */
  #pageLinks(pages) {
    return pages
      .map((page) => `<a href="${escapeHtml(this.#bookletLink(page))}" target="_blank" rel="noopener" data-booklet-link>${page}</a>`)
      .join(', ');
  }

  #renderGuide() {
    const steps = this.#steps;

    const navItems = steps
      .map((step, index) => `<li><a href="#step-${step.id}" data-step-link="${step.id}"><span class="lumvin-guide__nav-index" aria-hidden="true">${index + 1}</span>${escapeHtml(this.#stepTitle(step))}</a></li>`)
      .join('');

    const languageOptions = Object.keys(this.#config.languages)
      .map((code) => `<option value="${code}" lang="${code}"${code === this.#language ? ' selected' : ''}>${escapeHtml(lookup(this.#bundles[code], 'meta.name') ?? LANGUAGE_NAMES[code] ?? code)}</option>`)
      .join('');

    return `<div class="lumvin-guide__layout" id="LumvinGuideSteps">
      <aside class="lumvin-guide__sidebar" aria-label="${this.#attr('guide.progress')}">
        <div class="lumvin-guide__sidebar-inner">
          <div class="lumvin-guide__current">
            <span class="lumvin-guide__label">${this.#t('guide.material')}</span>
            <span class="lumvin-guide__current-name">${escapeHtml(this.#material.name)}</span>
            <button type="button" class="lumvin-guide__text-link" data-change-material>${this.#t('guide.changeMaterial')}</button>
          </div>
          <nav aria-label="${this.#attr('guide.progress')}">
            <ol class="lumvin-guide__nav" role="list">${navItems}</ol>
            <ul class="lumvin-guide__nav lumvin-guide__nav--extra" role="list">
              <li><a href="#step-help" data-step-link="help">${this.#t('help.heading')}</a></li>
              <li><a href="#LumvinGuideBooklet">${this.#t('booklet.title')}</a></li>
            </ul>
          </nav>
          <label class="lumvin-guide__select">
            <span class="lumvin-guide__label">${this.#t('guide.language')}</span>
            <select data-language-select>${languageOptions}</select>
          </label>
        </div>
      </aside>

      <div class="lumvin-guide__steps">
        <header class="lumvin-guide__official">
          <p class="lumvin-guide__official-badge">${icon('book')}${this.#t('official.heading')}</p>
          <p class="lumvin-guide__official-note">${this.#t('official.headingNote')}</p>
          <p class="lumvin-guide__source">${this.#t('official.materialPages', { pages: '' })}${this.#pageLinks(this.#materialPages)}</p>
          ${this.#misprinted ? `<div class="lumvin-guide__callout lumvin-guide__callout--caution" role="note">${icon('info')}<div><p>${this.#t('official.translated')}</p></div></div>` : ''}
        </header>

        ${steps
          .map((step, index) => {
            const next = steps[index + 1];
            const title = this.#stepTitle(step);
            const sources = this.#sourcePages(step)
              .map(({ page, label }) => `<a href="${escapeHtml(this.#bookletLink(page))}" target="_blank" rel="noopener" data-booklet-link>${escapeHtml(label)}</a>`)
              .join(', ');
            return `<section class="lumvin-guide__step" id="step-${step.id}" data-step="${step.id}" aria-labelledby="LumvinGuideStep-${step.id}">
              <header class="lumvin-guide__step-header">
                <p class="lumvin-guide__step-number">${this.#t('guide.step', { n: String(index + 1).padStart(2, '0') })}${step.optional ? ` <span class="lumvin-guide__badge">${this.#t('guide.optional')}</span>` : ''}</p>
                <h2 class="lumvin-guide__step-title" id="LumvinGuideStep-${step.id}" tabindex="-1">${escapeHtml(title)}</h2>
                <p class="lumvin-guide__source">${this.#t('official.source', { pages: '' })}${sources}</p>
              </header>
              <div class="lumvin-guide__step-body">
                ${this.#media(step.id)}
                ${step.blocks.map((block) => this.#renderBlock(block)).join('')}
                ${this.#video(step.id, title)}
              </div>
              ${next ? `<a class="lumvin-guide__next" href="#step-${next.id}" data-step-link="${next.id}">${this.#t('guide.next', { title: this.#stepTitle(next) })}${icon('arrowDown')}</a>` : ''}
            </section>`;
          })
          .join('')}

        ${this.#renderHelp()}
      </div>
    </div>

    <div class="lumvin-guide__bar" data-guide-bar>
      <label class="lumvin-guide__bar-language">
        <span class="visually-hidden">${this.#t('guide.language')}</span>
        <select data-language-select>${Object.keys(this.#config.languages)
          .map((code) => `<option value="${code}"${code === this.#language ? ' selected' : ''}>${code.toUpperCase()}</option>`)
          .join('')}</select>
      </label>
      <button type="button" class="lumvin-guide__bar-button" data-step-nav="-1" aria-label="${this.#attr('guide.previousStep')}">${icon('arrowUp')}</button>
      <div class="lumvin-guide__bar-status" aria-live="polite">
        <span class="lumvin-guide__bar-text" data-bar-text></span>
        <span class="lumvin-guide__bar-track" aria-hidden="true">${steps.map((step) => `<span data-bar-step="${step.id}"></span>`).join('')}</span>
      </div>
      <button type="button" class="lumvin-guide__bar-button" data-step-nav="1" aria-label="${this.#attr('guide.nextStep')}">${icon('arrowDown')}</button>
    </div>`;
  }

  #renderHelp() {
    const subject = encodeURIComponent(this.#rawT('help.emailSubject'));
    return `<section class="lumvin-guide__help" id="step-help" aria-labelledby="LumvinGuideHelpHeading">
      <h2 class="lumvin-guide__help-heading" id="LumvinGuideHelpHeading" tabindex="-1">${this.#t('help.heading')}</h2>
      <p class="lumvin-guide__help-note">${this.#t('help.note')}</p>
      <h3 class="lumvin-guide__help-title">${this.#t('help.title')}</h3>
      <p>${this.#t('help.text')}</p>
      <p class="lumvin-guide__label">${this.#t('help.includeTitle')}</p>
      <ul class="lumvin-guide__checklist" role="list">${(this.#raw('help.include') ?? []).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
      <div class="lumvin-guide__help-actions">
        <a class="button" href="${escapeHtml(this.#config.contactUrl)}" data-support-link="help">${this.#t('help.button')}</a>
        ${
          this.#config.email
            ? `<a class="lumvin-guide__text-link" href="mailto:${escapeHtml(this.#config.email)}?subject=${subject}" data-support-link="help-email">${this.#t('help.email', { email: this.#config.email })}</a>`
            : ''
        }
      </div>
    </section>`;
  }

  /** The original booklet, opened at the selected material's first page (or the language section). */
  #renderBooklet() {
    if (!this.#config.bookletUrl) return '';
    const page = this.#material ? this.#materialPages[0] : BOOKLET_PAGES.cover[this.#language];
    const url = this.#bookletLink(page);
    return `<section class="lumvin-guide__booklet" id="LumvinGuideBooklet" aria-labelledby="LumvinGuideBookletTitle">
      <h2 class="lumvin-guide__booklet-title" id="LumvinGuideBookletTitle">${this.#t('booklet.title')}</h2>
      <p>${this.#t('booklet.text')}</p>
      <a class="button button-secondary lumvin-guide__booklet-open" href="${escapeHtml(url)}" target="_blank" rel="noopener" data-booklet-link>${this.#t('booklet.open')}${icon('external')}</a>
      <div class="lumvin-guide__booklet-frame">
        <iframe src="${escapeHtml(url)}" title="${this.#attr('booklet.frameTitle')}" loading="lazy" allowfullscreen></iframe>
      </div>
    </section>`;
  }

  /** @param {Element} trigger */
  #openLightbox(trigger) {
    const dialog = this.querySelector('[data-lightbox]');
    const body = this.querySelector('[data-lightbox-body]');
    const caption = this.querySelector('[data-lightbox-caption]');
    const visual = trigger.querySelector('img');
    if (!(dialog instanceof HTMLDialogElement) || !body || !caption || !visual) return;

    const copy = /** @type {HTMLImageElement} */ (visual.cloneNode(true));
    copy.loading = 'eager';
    copy.sizes = '100vw';
    body.replaceChildren(copy);
    caption.textContent = trigger.closest('figure')?.querySelector('figcaption')?.textContent?.trim() ?? '';
    this.#lightboxTrigger = /** @type {HTMLElement} */ (trigger);
    dialog.showModal();
  }

  #closeLightbox() {
    const dialog = this.querySelector('[data-lightbox]');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  }

  #bindEvents() {
    const { signal } = this.#abort;

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);

        const languageButton = target.closest('[data-language]');
        if (languageButton) return this.#setLanguage(languageButton.getAttribute('data-language'));

        const materialButton = target.closest('[data-material]');
        if (materialButton) return this.#setMaterial(materialButton.getAttribute('data-material'));

        if (target.closest('[data-change-material]')) {
          return this.querySelector('#LumvinGuideMaterialHeading')?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth' });
        }

        const stepLink = target.closest('[data-step-link]');
        if (stepLink) {
          event.preventDefault();
          return this.#scrollToStep(stepLink.getAttribute('data-step-link'));
        }

        const stepNav = target.closest('[data-step-nav]');
        if (stepNav) return this.#moveStep(Number(stepNav.getAttribute('data-step-nav')));

        const videoButton = target.closest('[data-video-embed]');
        if (videoButton) return this.#playVideo(videoButton);

        const zoom = target.closest('[data-zoom]');
        if (zoom) return this.#openLightbox(zoom);

        if (target.closest('[data-lightbox-close]') || target.matches('[data-lightbox]')) return this.#closeLightbox();

        const bookletLink = target.closest('[data-booklet-link]');
        if (bookletLink) this.#track('original_guide_opened', { href: bookletLink.getAttribute('href') });

        const supportLink = target.closest('[data-support-link]');
        if (supportLink) this.#track('contact_support_clicked', { location: supportLink.getAttribute('data-support-link') });
      },
      { signal }
    );

    this.addEventListener(
      'change',
      (event) => {
        const select = /** @type {HTMLElement} */ (event.target).closest('[data-language-select]');
        if (select instanceof HTMLSelectElement) this.#setLanguage(select.value);
      },
      { signal }
    );

    // `close` does not bubble: return focus to the image that opened the viewer.
    this.addEventListener(
      'close',
      (event) => {
        if (!(event.target instanceof HTMLDialogElement)) return;
        this.#lightboxTrigger?.focus();
        this.#lightboxTrigger = null;
      },
      { signal, capture: true }
    );
  }

  /** @param {string | null} language */
  async #setLanguage(language) {
    if (!language || language === this.#language || !this.#config.languages[language]) return;

    const previous = this.#language;
    const step = this.#activeStep;
    try {
      await this.#loadBundles(language);
    } catch {
      return;
    }

    this.#language = language;
    writeStorage(STORAGE_KEYS.language, language);
    this.#updateUrl();
    this.#render();
    this.#track('language_selected', { previous_language: previous });

    if (this.#material && step) {
      this.#scrollToStep(step, false);
    } else {
      this.querySelector(`[data-language="${language}"]`)?.focus();
    }
  }

  /** @param {string | null} id */
  #setMaterial(id) {
    const material = MATERIALS.find((entry) => entry.id === id);
    if (!material) return;

    const changed = material.id !== this.#material?.id;
    this.#material = material;
    writeStorage(STORAGE_KEYS.material, material.id);
    this.#updateUrl();

    if (changed) {
      this.#viewedSteps.clear();
      this.#activeStep = '';
      this.#render();
      this.#track('material_selected', {});
    }
    this.#scrollToStep(this.#steps[0].id);
  }

  /**
   * @param {string | null} id
   * @param {boolean} [userAction] - Animate and move focus to the heading (not on page load).
   */
  #scrollToStep(id, userAction = true) {
    const section = id && this.querySelector(`#step-${CSS.escape(id)}`);
    if (!section) return;

    section.scrollIntoView({ behavior: userAction && !reducedMotion() ? 'smooth' : 'auto', block: 'start' });
    if (userAction) section.querySelector('h2')?.focus({ preventScroll: true });
    history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}#step-${id}`);
  }

  /** @param {number} direction */
  #moveStep(direction) {
    const ids = [...this.#steps.map((step) => step.id), 'help'];
    const index = ids.indexOf(this.#activeStep || ids[0]);
    const next = ids[Math.min(ids.length - 1, Math.max(0, index + direction))];
    this.#scrollToStep(next);
  }

  /** @param {Element} button */
  #playVideo(button) {
    const iframe = document.createElement('iframe');
    iframe.src = button.getAttribute('data-video-embed') || '';
    iframe.title = button.getAttribute('data-video-title') || '';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';
    const wrapper = button.closest('.lumvin-guide__video');
    wrapper?.classList.add('is-playing');
    button.replaceWith(iframe);
    this.#track('installation_video_played', { slot: wrapper?.getAttribute('data-video-slot') });
  }

  /** Keeps ?lang and ?material in the URL; every other parameter (UTM tags) is preserved. */
  #updateUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', this.#language);
    if (this.#material) url.searchParams.set('material', this.#material.id);
    history.replaceState(history.state, '', url);
  }

  #observeSteps() {
    this.#observer?.disconnect();
    const sections = this.querySelectorAll('.lumvin-guide__step, .lumvin-guide__help');
    if (!sections.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = entry.target.getAttribute('data-step') || 'help';
          this.#setActiveStep(id);
          const key = `${this.#material?.id}:${id}`;
          if (!this.#viewedSteps.has(key)) {
            this.#viewedSteps.add(key);
            this.#track('installation_step_viewed', { step: id });
          }
        }
      },
      // A step is "current" while it crosses the upper third of the viewport.
      { rootMargin: '-30% 0px -65% 0px' }
    );
    sections.forEach((section) => this.#observer?.observe(section));

    // The mobile progress bar only shows while the steps are on screen.
    const layout = this.querySelector('#LumvinGuideSteps');
    const bar = this.querySelector('[data-guide-bar]');
    if (layout && bar) {
      this.#barObserver?.disconnect();
      this.#barObserver = new IntersectionObserver(([entry]) => bar.classList.toggle('is-visible', entry.isIntersecting));
      this.#barObserver.observe(layout);
    }
  }

  /** @param {string} id */
  #setActiveStep(id) {
    this.#activeStep = id;
    const steps = this.#steps;
    const index = steps.findIndex((step) => step.id === id);

    this.querySelectorAll('[data-step-link]').forEach((link) => {
      if (link.getAttribute('data-step-link') === id) link.setAttribute('aria-current', 'step');
      else link.removeAttribute('aria-current');
    });

    this.querySelectorAll('[data-bar-step]').forEach((segment, segmentIndex) => {
      segment.classList.toggle('is-done', index === -1 || segmentIndex < index);
      segment.classList.toggle('is-active', segmentIndex === index);
    });

    const barText = this.querySelector('[data-bar-text]');
    if (barText) {
      barText.textContent =
        index === -1
          ? this.#rawT('help.heading')
          : `${this.#rawT('guide.stepOf', { n: index + 1, total: steps.length })} · ${this.#stepTitle(steps[index])}`;
    }
  }

  /**
   * Analytics hook. Payloads never include personal data.
   * @param {string} name
   * @param {object} detail
   */
  #track(name, detail) {
    const payload = { ...detail, language: this.#language, material: this.#material?.id ?? null };
    this.dispatchEvent(new CustomEvent('lumvin:install-guide', { bubbles: true, detail: { name, ...payload } }));
    try {
      window.Shopify?.analytics?.publish?.(`lumvin_install_guide_${name}`, payload);
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `lumvin_install_guide_${name}`, ...payload });
    } catch {
      // Analytics must never break the guide.
    }
  }
}

if (!customElements.get('lumvin-install-guide')) {
  customElements.define('lumvin-install-guide', LumvinInstallGuide);
}
